import styled from "styled-components";
import { FC, MouseEvent, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";

import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { ActionsWrap, Td } from "@/components/Table";
import { ReactComponent as PencilIcon } from "@/assets/icons/pencil.svg";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import Text from "@/components/Text";
import TableButton from "./TableButton";
import ActorsModal from "./ActorsModal";
import { ProductionSceneActorResource } from "@/types/resources";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";

const SceneNumber = styled(Text)`
  background: ${({ theme }) => theme.colors.lightGray};
  width: 16px;
  height: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 6px;
`;

const TitleHolder = styled.div`
  display: flex;
  align-items: flex-start;
`;

const TextWithSvg = styled.div`
  display: flex;
  svg {
    margin-right: 6px;
    transform: translateY(-1px);
  }
`;

interface Props {
  actor: ProductionSceneActorResource;
  sceneIndex: number;
}

const ActorsTableRow: FC<Props> = ({ actor, sceneIndex }) => {
  const [showModal, setShowModal] = useState(false);

  const { projectId } = useParams();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        "Are you sure you want to delete this actor?"
      );

      if (confirm) {
        await api.delete<never, { id: number }>(
          `/v1/production/scenes/actors/${actor.id}`
        );
        alerts.success("Actor successfully deleted");

        queryClient.invalidateQueries(["production_projects", { projectId }]);
      }
    } catch (err) {
      alerts.error();
    }
  };

  return (
    <tr>
      <Td>
        <TitleHolder>
          <SceneNumber $type="extraSmall">{sceneIndex}</SceneNumber>
          {actor.role}
        </TitleHolder>
      </Td>
      <Td>
        {actor.name ? (
          <>{actor.name}</>
        ) : (
          <TableButton text="Add assignee" onClick={() => setShowModal(true)} />
        )}
      </Td>
      <Td>
        {actor.time ? (
          <TextWithSvg>
            <svg
              width="15"
              height="16"
              viewBox="0 0 15 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.2697 2.28516H1.73122C1.41259 2.28516 1.1543 2.54099 1.1543 2.85658V14.2852C1.1543 14.6007 1.41259 14.8566 1.73122 14.8566H13.2697C13.5883 14.8566 13.8466 14.6007 13.8466 14.2852V2.85658C13.8466 2.54099 13.5883 2.28516 13.2697 2.28516Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.9609 1.14062V3.42634"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4.03906 1.14062V3.42634"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M1.1543 5.71484H13.8466"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {DateTime.fromISO(actor.time).toFormat("dd/LL/yyyy HH:mm")}
          </TextWithSvg>
        ) : (
          <TableButton
            text="Add date & time"
            icon="date"
            onClick={() => setShowModal(true)}
          />
        )}
      </Td>
      <Td>
        {actor.note ? (
          <>{actor.note}</>
        ) : (
          <TableButton text="Add note" onClick={() => setShowModal(true)} />
        )}
      </Td>
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton
              as="button"
              onClick={() => setShowModal(true)}
            >
              <span>
                <PencilIcon />
              </span>
              Edit
            </ActionsDropdownButton>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
      </Td>
      <AnimatePresence>
        {!!showModal && (
          <ActorsModal actor={actor} onClose={() => setShowModal(false)} />
        )}
      </AnimatePresence>
    </tr>
  );
};

export default ActorsTableRow;
